// Plock (mockup picking-v1 with the owner's answers, 2026-10-28): pick a customer order onto
// outbound pallets. The order's lines are offered in walking order with the FEFO lot Trail
// chose; a scanned carton is accepted whichever line is shown and lands on the line whose
// product it carries; a lot other than the proposed one warns and asks for a reason; a stock
// pallet scanned whole picks every carton on it; a carton holding more than a line needs is
// split and the piece gets the lot with a suffix. "Pall klar" records the pallet (Packed,
// Picked per carton, Split, Aggregated) and either opens the next pallet or finishes the
// order, which is then reported picked to the server for the ERP.

import { PICK, applyInput, classOf, identityKey, parseScan, startRun, uuidv7, type EventEnvelope, type GS1Elements, type RunState, type ScanInput, isBottomFirst } from "@trail/core";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Admin, type ErpReportOut, ApiError, type BundleProduct, Api, getSession, effectiveSettings as getSettings, type ContainerSummary, type PickLineProposal, type StockRow, type SalesOrderDetail, type SalesOrderSummary, type StepBatch, type AutomationEventIn } from "../api/client.js";
import { erpLabel, refreshOrderNow, useErpRefresh, useErpRuns } from "../api/erp-refresh.js";
import { CameraView } from "../CameraView.js";
import { uomLabel, fmtNum, fmtQty, t } from "../i18n.js";
import { pending, startQueueWorker, submitStep, subscribe, type QueuedStep } from "../receive/queue.js";
import { findLocation, buildRegistry, cacheBundle, isBulkProduct, loadCachedBundle, plainElements, type Registry } from "../receive/registry.js";
import { isGs1Symbology, type CodeState } from "../scan/camera.js";
import { feedback, unlockAudio } from "../scan/feedback.js";
import { attachWedge } from "../scan/wedge.js";
import { Actions, Banner, Btn, Chips, Entry, ListItem, Prompt, Rows, ScanBar, Sheet, Strip, focusScan, type BannerData, type RowData } from "../ui/components.jsx";
import { GotoScene } from "../ui/GotoScene.jsx";
import { reasonsForShort } from "../receive/reasons.js";
import { groupOrders } from "./queue.js";

const FLOW = PICK;
const SCHEME = "gtin+lot" as const;
/** Registering without a scan is a site setting per flow (owner 2026-11-01): off, with a comment
 *  for the roles listed, or leads only. A device without a user session follows the role list. */
export function canManual(manual: { roles?: string[]; receive?: string; pick?: string } | undefined, flow: "receive" | "pick" = "pick"): boolean {
  const mode = (flow === "receive" ? manual?.receive : manual?.pick) ?? "comment";
  if (mode === "off") return false;
  const session = getSession();
  if (mode === "lead") return !session || (session.roles ?? []).some((r) => r === "lead" || r === "administrator") || session.permissions.includes("config.edit");
  if (!session) return true;
  const allowed = manual?.roles ?? ["operator", "lead", "quality", "administrator"];
  return (session.roles ?? []).some((r) => allowed.includes(r)) || session.permissions.includes("config.edit");
}
const shortSscc = (sscc: string) => `${sscc.slice(0, 4)}…${sscc.slice(-4)}`;
const num = (s: string) => Number(s);
const str = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/\.?0+$/, ""));

/** One carton taken for a line, as recorded locally until the pallet is closed. */
/** What the done screen shows: every line with picked of ordered, the short ones with reason. */
interface DoneSummary {
  pallets: number;
  kolli: number;
  short: number;
  lines: { name: string; sku: string | null; got: number; ordered: number; uom: string; short: number; reason: string | null; orderRef: string | null }[];
  /** The orders reported, when several were picked together. */
  orders: string[];
  /** When the order was finished (the report's verdict is looked for after this). */
  at: string;
  /** The ERP's verdict when the server waited for it: "sent" shows at once, "unknown" is polled for. */
  verdict?: "sent" | "unknown";
  /** The ERP refused the pick (or holds the order): the order stays as picking, nothing printed; the picker tries again. */
  refused?: { verdict: string; message: string; retry: () => void } | null;
}

/** The ERP's verdict on the pick report, within seconds of "klar" (owner 2026-11-03: the picker must know at
 *  once when a report does not go through). Polls the report log for the order's pick report: waiting,
 *  reported, held (the order is open in the ERP), or refused with the ERP's words. */
function ReportVerdict({ refs, since, verdict }: { refs: string[]; since: string; verdict?: "sent" | "unknown" | undefined }) {
  if (verdict === "sent") return <Banner kind="ok" label={t("pick.reportSent")} message={t("pick.reportSentMsg")} />;
  return <ReportVerdictPoll refs={refs} since={since} />;
}
function ReportVerdictPoll({ refs, since }: { refs: string[]; since: string }) {
  const [rows, setRows] = useState<ErpReportOut[] | null>(null);
  const [gaveUp, setGaveUp] = useState(false);
  const said = useRef<string | null>(null);
  useEffect(() => {
    let stop = false;
    let n = 0;
    const tick = async () => {
      try {
        const r = await Admin.erpReports({ kind: "sales-order.picked", since, limit: 20 });
        if (stop) return;
        setRows(r.items.filter((x) => refs.includes(x.ref)));
      } catch { /* offline: the verdict comes later under Drift → Affärssystem */ }
    };
    void tick();
    const h = setInterval(() => { n++; void tick(); if (n >= 20) { clearInterval(h); setGaveUp(true); } }, 2000);
    return () => { stop = true; clearInterval(h); };
  }, [refs.join(","), since]);
  const settled = rows && refs.every((ref) => rows.some((x) => x.ref === ref && (x.state === "sent" || x.state === "failed" || x.state === "shadow")));
  const failed = rows?.filter((x) => x.state === "failed") ?? [];
  const locked = rows?.filter((x) => x.state === "pending" && (x.result as { locked?: unknown } | null)?.locked) ?? [];
  useEffect(() => {
    const key = failed.length ? "failed" : locked.length ? "locked" : settled ? "sent" : null;
    if (key && said.current !== key) { said.current = key; feedback(key === "sent" ? "accepted" : "rejected"); }
  }, [failed.length, locked.length, settled]);
  if (failed.length) return <Banner kind="bad" label={t("pick.reportRefused")} message={failed.map((x) => `${x.ref}: ${(x.result as { error?: string } | null)?.error ?? "?"}`).join(" · ")} />;
  if (locked.length) return <Banner kind="warn" label={t("pick.reportLocked")} message={t("pick.reportLockedMsg", { refs: locked.map((x) => x.ref).join(", ") })} />;
  if (settled) return <Banner kind="ok" label={rows!.some((x) => x.state === "shadow") ? t("pick.reportShadow") : t("pick.reportSent")} message={t("pick.reportSentMsg")} />;
  if (gaveUp) return <Banner kind="warn" label={t("pick.reportLate")} message={t("pick.reportLateMsg")} />;
  return <Banner kind="warn" label={t("pick.reportWaiting")} message={t("pick.reportWaitingMsg")} />;
}

interface Take {
  containerId: string;
  lineNo: number;
  lot: string | null;
  quantity: number;
  uom: string;
  overrideReason: string | null;
  /** The comment of a pick without a scan (owner 2026-11-01): capture manual, a deviation for the lead. */
  manual: string | null;
  /** When the carton was cut: the piece's identity and what stays in the parent. */
  split: { parentContainerId: string; identity: { key: string | null; ai: Record<string, string> }; remaining: number } | null;
  /** The stock pallet the carton came off, when it was lifted from one. */
  fromPalletId: string | null;
  /** The outbound pallet it was closed on (set when the pallet is closed). */
  palletId: string | null;
  at: string;
  /** Where it stood, and the top-level container that left the location with it (the carton itself,
   *  or the whole stock pallet) — null when the pallet it sat on stays. For the zero-point. */
  fromLocationId: string | null;
  leaves: string | null;
}

interface Pallet {
  containerId: string;
  sscc: string;
  /** Issued here (Packed is recorded) or an existing packed pallet scanned again. */
  fresh: boolean;
}

/** Several orders to the same customer and carrier picked as one round onto shared pallets
 *  (owner 2026-11-01, decided before the first carton; mockup picking-v1/combined-pallets). The
 *  run sees one merged order whose line numbers are offset per order; events and reports go
 *  back to each order by its own line numbers. */
interface PickGroup { orders: { id: string; reference: string; offset: number; detail: SalesOrderDetail }[] }
const GROUP_OFFSET = 10000;
const origOf = (group: PickGroup | null, lineNo: number): { id: string; reference: string; lineNo: number } | null => {
  if (!group) return null;
  const o = group.orders[Math.floor(lineNo / GROUP_OFFSET)];
  return o ? { id: o.id, reference: o.reference, lineNo: lineNo - o.offset } : null;
};
function mergeGroup(details: SalesOrderDetail[], proposals: PickLineProposal[][]): { order: SalesOrderDetail; proposal: PickLineProposal[]; group: PickGroup } {
  const group: PickGroup = { orders: details.map((d, i) => ({ id: d.id, reference: d.reference, offset: i * GROUP_OFFSET, detail: d })) };
  const primary = details[0]!;
  const order: SalesOrderDetail = { ...primary, reference: details.map((d) => d.reference).join(" + "), lines: details.reduce((n, d) => n + d.lineItems.length, 0), lineItems: details.flatMap((d, i) => d.lineItems.map((l) => ({ ...l, lineNo: l.lineNo + i * GROUP_OFFSET }))) };
  const proposal = proposals.flatMap((p, i) => p.map((x) => ({ ...x, lineNo: x.lineNo + i * GROUP_OFFSET })));
  return { order, proposal, group };
}

export function Pick({ onClose }: { onClose: () => void }) {
  const settings = useMemo(getSettings, []);
  const [registry, setRegistry] = useState<Registry | null>(() => {
    const cached = loadCachedBundle();
    return cached ? buildRegistry(cached) : null;
  });
  const [queue, setQueue] = useState<QueuedStep[]>(pending);
  const [online, setOnline] = useState(navigator.onLine);
  const [orders, setOrders] = useState<SalesOrderSummary[] | null>(null);
  const [order, setOrder] = useState<SalesOrderDetail | null>(null);
  const [proposal, setProposal] = useState<PickLineProposal[] | null>(null);
  const [done, setDone] = useState<DoneSummary | null>(null);
  const [carry, setCarry] = useState<BannerData | null>(null);
  const [seq, setSeq] = useState(0);
  /** Work queues (owner 2026-11-01): the server filters by the device's or user's subscriptions; a lead may lift it. */
  const [allQueues, setAllQueues] = useState(false);
  const [group, setGroup] = useState<PickGroup | null>(null);
  /** The "Samlasta med?" step: the opened order, and the other orders to the same customer and carrier. */
  const [combine, setCombine] = useState<{ primary: SalesOrderDetail; proposal: PickLineProposal[]; mates: SalesOrderSummary[]; chosen: Set<string> } | null>(null);
  const combinedAllowed = registry?.bundle.siteConfig?.picking?.combinedPallets !== false;
  /** Orders in the list that could share a pallet with `o`: same customer, same carrier, not yet picked. */
  const matesOf = (o: { id: string; customer: { id: string } | null; carrier: string | null }) => combinedAllowed && o.customer
    ? (orders ?? []).filter((x) => x.id !== o.id && x.customer?.id === o.customer!.id && (x.carrier ?? "").toLowerCase() === (o.carrier ?? "").toLowerCase() && x.status !== "picked" && x.status !== "shipped")
    : [];

  useEffect(() => {
    startQueueWorker();
    Api.bundle().then((b) => { cacheBundle(b); setRegistry(buildRegistry(b)); }).catch(() => undefined);
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    const unsub = subscribe(setQueue);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); unsub(); };
  }, []);
  useEffect(() => {
    if (!order) Api.salesOrders("open,picking", allQueues).then(setOrders).catch(() => setOrders([]));
  }, [order, seq, allQueues]);
  // The order list asks the ERP mirror to run now and reloads when it has (owner 2026-10-30).
  const erp = useErpRefresh("sales-orders", () => setSeq((s) => s + 1), !order);
  const erpLine = erpLabel(erp, t as never);

  const net = { online, queued: queue.length, stuck: queue.filter((q) => q.lastError && !/fetch|network|abort|5\d\d|401|429/i.test(q.lastError)).length };

  const [syncing, setSyncing] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [erpNotice, setErpNotice] = useState<BannerData | null>(null);
  // An order number Trail does not list: ask the ERP by number, then look again; an order the ERP
  // has but outside the pick rule (confirmed for another day) is said so.
  const fetchFromErp = async (ref: string) => {
    const r = await refreshOrderNow("sales-orders", ref);
    const list = await Api.salesOrders();
    setOrders(list);
    const hit = list.find((o) => o.reference.toUpperCase() === ref.toUpperCase()) ?? null;
    let outsideRule = false;
    if (!hit && r.mirrored) {
      try { outsideRule = (await Api.salesOrders("cancelled")).some((o) => o.reference.toUpperCase() === ref.toUpperCase()); } catch { outsideRule = false; }
    }
    return { ...r, hit, outsideRule };
  };
  // While picking: when the mirror has run again, re-read the order and say so if it changed.
  useErpRuns(!!order && !done && !group, () => {
    if (!order) return;
    Api.salesOrder(order.id).then((o) => {
      // The mirror's own bookkeeping on the order (what the ERP said, when it was read) is not a change
      // to the work (owner 2026-11-02: the notice came up every sync with nothing changed).
      // Only what the work depends on counts as a change: the lines, the customer, dates and carrier, or a
      // cancellation. Not the mirror's bookkeeping (external.erpState), nor what this phone itself set when
      // it opened the order (status "picking", who picks) — both made the notice fire with nothing changed.
      const work = (x: typeof o) => JSON.stringify({ lines: x.lineItems, customer: x.customer, deliveryDate: x.deliveryDate, carrier: x.carrier, minShelfLife: x.minShelfLifeDays, cancelled: x.status === "cancelled" });
      if (work(o) === work(order)) return;
      setOrder(o);
      Api.pickProposal(o.id).then((p) => setProposal(p.lines)).catch(() => undefined);
      setErpNotice({ kind: "warn", label: t("erp.orderChanged"), message: t("erp.orderChanged.msg") });
    }).catch(() => undefined);
  });
  const open = async (id: string) => {
    if (opening) return;
    setOpening(id);
    try {
      // Company setting: read the order from the ERP again before picking starts (owner 2026-10-30).
      if ((registry?.bundle.tenantConfig?.erp?.refreshOrderBeforeWork ?? true) && erp?.mirrored) {
        const ref = orders?.find((x) => x.id === id)?.reference;
        if (ref) {
          setSyncing(t("erp.readingOrder", { ref }));
          const r = await refreshOrderNow("sales-orders", ref);
          setSyncing(null);
          if (!r.fresh && r.mirrored) setCarry({ kind: "warn", label: t("erp.notReached"), message: t("erp.notReached.msg") });
        }
      }
      const [o, p] = await Promise.all([Api.salesOrder(id), Api.pickProposal(id)]);
      // Locked in the ERP (owner 2026-11-03: someone has it open in Visma): the server says no, and so do we.
      if (o.erpLock) { feedback("rejected"); setCarry({ kind: "bad", label: t("erp.locked"), message: t("erp.lockedMsg", { ref: o.reference, process: o.erpLock.process, by: o.erpLock.by ?? "" }) }); return; }
      try { await Api.startPicking(id); } catch (e) { if (e instanceof ApiError && e.status === 409) { feedback("rejected"); setCarry({ kind: "bad", label: t("erp.locked"), message: (e.body as { message?: string } | null)?.message ?? t("erp.lockedMsg", { ref: o.reference, process: "?", by: "" }) }); return; } }
      setDone(null);
      setCarry(null);
      setErpNotice(null);
      setGroup(null);
      // Other orders to the same customer with the same carrier: decide before the first carton
      // whether they are picked together (owner 2026-11-01). Same delivery date is ticked by default.
      const mates = matesOf(o);
      if (mates.length > 0) { setCombine({ primary: o, proposal: p.lines, mates, chosen: new Set(mates.filter((m) => m.deliveryDate === o.deliveryDate).map((m) => m.id)) }); return; }
      setOrder(o);
      setProposal(p.lines);
    } catch (e) {
      setSyncing(null);
      setCarry({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
    } finally {
      setOpening(null);
    }
  };

  if (!registry) {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.pick")}</b>} {...net} />
        <div className="body"><p className="hint">{t("common.loading")}</p></div>
        <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
      </div>
    );
  }
  /** Start the round: the primary alone, or merged with the chosen orders. */
  const startRound = async (c: NonNullable<typeof combine>, ids: string[]) => {
    setOpening(c.primary.id);
    try {
      const mates = c.mates.filter((m) => ids.includes(m.id));
      const loaded = await Promise.all(mates.map(async (m) => { const [d, p] = await Promise.all([Api.salesOrder(m.id), Api.pickProposal(m.id)]); Api.startPicking(m.id).catch(() => undefined); return { d, p: p.lines }; }));
      if (loaded.length === 0) { setGroup(null); setOrder(c.primary); setProposal(c.proposal); }
      else { const m = mergeGroup([c.primary, ...loaded.map((x) => x.d)], [c.proposal, ...loaded.map((x) => x.p)]); setGroup(m.group); setOrder(m.order); setProposal(m.proposal); }
      setCombine(null);
    } catch (e) {
      setCarry({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
    } finally { setOpening(null); }
  };
  if (combine && !order) {
    const c = combine;
    const chosenMates = c.mates.filter((m) => c.chosen.has(m.id));
    const totalLines = c.primary.lineItems.length + chosenMates.reduce((n, m) => n + m.lines, 0);
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.pick")}</b>} middle={`${c.primary.reference} · ${c.primary.customer?.name ?? ""}`} {...net} />
        <div className="body">
          <Prompt step={t("pick.step1")} title={t("pick.combineTitle")} sub={t("pick.combineSub", { customer: c.primary.customer?.name ?? "", carrier: c.primary.carrier ?? "—" })} />
          {carry && <Banner {...carry} />}
          <div className="list">
            {c.mates.map((m) => (
              <ListItem key={m.id} title={`${c.chosen.has(m.id) ? "☑" : "☐"} ${m.reference}`} desc={`${m.lines} rader · ${m.deliveryDate === c.primary.deliveryDate ? (m.deliveryDate === new Date().toISOString().slice(0, 10) ? t("pick.today") : m.deliveryDate ?? "") : `${m.deliveryDate ?? ""} · ${t("pick.combineOtherDate")}`}`} onClick={() => { const next = new Set(c.chosen); if (next.has(m.id)) next.delete(m.id); else next.add(m.id); setCombine({ ...c, chosen: next }); }} />
            ))}
          </div>
          <p className="hint">{t("pick.combineSummary", { lines: totalLines })}</p>
        </div>
        <Actions>
          <Btn kind="quiet" disabled={!!opening} onClick={() => void startRound(c, [])}>{t("pick.combineOnly", { ref: c.primary.reference })}</Btn>
          <Btn kind="primary" disabled={!!opening || chosenMates.length === 0} onClick={() => void startRound(c, chosenMates.map((m) => m.id))}>{t("pick.combinePick", { n: chosenMates.length + 1 })}</Btn>
        </Actions>
      </div>
    );
  }
  if (done && order) {
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.pick")}</b>} middle={`${order.reference} · ${order.customer?.name ?? ""}`} {...net} />
        <div className="body">
          <Prompt step={t("pick.step1").replace("1", "3")} title={done.refused ? t("pick.refusedTitle") : t("pick.done")} />
          <div className="glance"><span className="n">{done.kolli}</span><span className="of">{t("pick.kolli")} · {order.lineItems.length} rader · {done.pallets} pallar</span></div>
          {done.refused && <Banner kind="bad" label={done.refused.verdict === "locked" ? t("erp.locked") : t("pick.reportRefused")} message={t("pick.refusedMsg", { msg: done.refused.message })} />}
          {!done.refused && <Banner kind={done.short > 0 ? "warn" : "ok"} label={done.short > 0 ? t("pick.doneShort") : t("pick.done")} message={`${done.orders.length > 1 ? `${t("pick.doneOrders", { refs: done.orders.join(", ") })} ` : ""}${t("pick.doneMsg", { pallets: done.pallets, kolli: done.kolli })}${done.short > 0 ? ` ${done.short === 1 ? t("pick.doneShortMsgOne") : t("pick.doneShortMsg", { n: done.short })}` : ""}`} />}
          {carry && <Banner {...carry} />}
          {!done.refused && <ReportVerdict refs={done.orders.length ? done.orders : [order.reference]} since={done.at} verdict={done.verdict} />}
          <Rows rows={done.lines.map((l): RowData => ({ k: l.name, small: [l.orderRef ? t("pick.orderTag", { ref: l.orderRef }) : null, l.sku, l.short > 0 ? t("pick.short", { n: fmtQty(l.short, l.uom) }) : null, l.short > 0 ? l.reason : null].filter(Boolean).join(" · "), q: `${fmtNum(l.got)} / ${fmtQty(l.ordered, l.uom)}`, tone: l.short > 0 ? "warn" : "ok" }))} />
        </div>
        <Actions>
          {done.refused ? (
            <>
              <Btn kind="quiet" icon="back" onClick={onClose}>{t("pick.toOutbound")}</Btn>
              <Btn kind="primary" onClick={() => done.refused!.retry()}>{t("pick.retryReport")}</Btn>
            </>
          ) : (registry.bundle.siteConfig?.picking?.afterOrder ?? "menu") === "next" ? (
            <>
              <Btn kind="quiet" icon="back" onClick={onClose}>{t("pick.toOutbound")}</Btn>
              <Btn kind="primary" onClick={() => { setOrder(null); setProposal(null); setDone(null); setSeq((s) => s + 1); }}>{t("pick.nextOrder")}</Btn>
            </>
          ) : (
            <>
              <Btn kind="quiet" onClick={() => { setOrder(null); setProposal(null); setDone(null); setSeq((s) => s + 1); }}>{t("pick.nextOrder")}</Btn>
              <Btn kind="primary" icon="back" onClick={onClose}>{t("pick.toOutbound")}</Btn>
            </>
          )}
        </Actions>
      </div>
    );
  }
  if (order && proposal) {
    return (
      <Run
        key={order.id}
        registry={registry}
        settings={settings}
        order={order}
        proposal={proposal}
        group={group}
        net={net}
        notice={erpNotice}
        onReproposal={() => {
          if (!group) { Api.pickProposal(order.id).then((p) => setProposal(p.lines)).catch(() => undefined); return; }
          Promise.all(group.orders.map((o) => Api.pickProposal(o.id))).then((ps) => setProposal(mergeGroup(group.orders.map((o) => o.detail), ps.map((p) => p.lines)).proposal)).catch(() => undefined);
        }}
        onDone={(summary, banner) => { setDone(summary); setCarry(banner); }}
        onAbort={() => { for (const id of group ? group.orders.map((o) => o.id) : [order.id]) Api.releasePicking(id).catch(() => undefined); setOrder(null); setProposal(null); setGroup(null); setSeq((s) => s + 1); }}
      />
    );
  }
  // The tapped order shows its own spinner and text (owner 2026-11-01: a line above the list is missed).
  const openingText = syncing ?? t("common.loading");
  return (
    <div className="screen" onClick={unlockAudio}>
      <Strip left={<b>{t("menu.pick")}</b>} {...net} />
      <div className="body">
        <Prompt step={t("pick.step1")} title={t("pick.title1")} sub={t("pick.sub1")} />
        {(registry.bundle.siteConfig?.picking?.queues ?? []).length > 0 && <Chips options={[{ value: "mine", label: t("pick.myQueues") }, { value: "all", label: t("pick.allQueues") }]} value={allQueues ? "all" : "mine"} onChange={(v) => setAllQueues(v === "all")} />}
        {carry && <Banner {...carry} />}
        <OrderScan orders={orders ?? []} onPick={(o) => void open(o.id)} onFetch={fetchFromErp} />
        {erpLine && !syncing && <p className="hint" style={{ marginTop: 4 }}>{erpLine}</p>}
        {orders?.length === 0 && <p className="hint">{t("pick.noOrders")}</p>}
        {/* Grouped per carrier when the site setting says so (owner 2026-10-29), earliest pickup first. */}
        {groupOrders(orders ?? [], registry.bundle.siteConfig?.picking).map((g, gi) => (
          <div key={g.carrier ?? `-${gi}`}>
            {(registry.bundle.siteConfig?.picking?.queue ?? "flat") === "carrier" && (
              <h2 className="sec">{g.carrier ?? t("pick.noCarrier")}{g.pickupTime ? ` · ${t("pick.pickupAt", { time: g.pickupTime })}` : ""} · {g.orders.length}</h2>
            )}
            <div className="list">
              {g.orders.map((o) => (
                <ListItem key={o.id} title={o.reference} tag={o.erpLock ? t("erp.lockedTag") : undefined} tagKind={o.erpLock ? "bad" : undefined} desc={opening === o.id ? <b>{openingText}</b> : `${o.erpLock ? `${t("erp.lockedTag")} · ` : ""}${o.customer?.name ?? o.source} · ${o.deliveryDate === new Date().toISOString().slice(0, 10) ? t("pick.today") : o.deliveryDate ?? ""} · ${o.lines} rader${o.queue ? ` · ${o.queue}` : ""}${o.carrier && (registry.bundle.siteConfig?.picking?.queue ?? "flat") !== "carrier" ? ` · ${o.carrier}` : ""}${matesOf(o).length > 0 ? ` · ${t("pick.sameCustomer", { n: matesOf(o).length })}` : ""}${o.pickupAt ? ` · ${new Date(o.pickupAt).toLocaleTimeString("sv-SE", { hour: "2-digit", minute: "2-digit" })}` : ""}`} right={opening === o.id ? <span className="spin" aria-label={openingText} /> : undefined} onClick={opening ? undefined : (() => void open(o.id))} />
              ))}
            </div>
          </div>
        ))}
      </div>
      <Actions><Btn kind="quiet" icon="back" onClick={onClose}>{t("common.menu")}</Btn></Actions>
    </div>
  );
}

/** The order-number scan field on the list: a wedge or camera scan of the reference opens the order. */
function OrderScan({ orders, onPick, onFetch }: { orders: SalesOrderSummary[]; onPick: (o: SalesOrderSummary) => void; onFetch: (ref: string) => Promise<{ mirrored: boolean; hit: SalesOrderSummary | null; outsideRule: boolean }> }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState<{ busy: boolean; text: string } | null>(null);
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    focusScan(el);
    return attachWedge(el, {
      onScan: (d) => {
        const ref = d.replace(/^\]\w\w/, "").trim().toUpperCase();
        const hit = orders.find((o) => o.reference.toUpperCase() === ref);
        if (hit) { feedback("accepted"); setNote(null); onPick(hit); return; }
        // Not in today's list: the ERP may have it (owner 2026-10-30) — digits only are order numbers.
        if (/^\d{3,12}$/.test(ref)) {
          setNote({ busy: true, text: t("erp.fetchingOrder", { ref }) });
          void onFetch(ref).then((r) => {
            if (r.hit) { feedback("accepted"); setNote(null); onPick(r.hit); return; }
            feedback("rejected");
            setNote({ busy: false, text: r.outsideRule ? t("erp.orderOutsideRule", { ref }) : r.mirrored ? t("erp.orderNotInErp.sales", { ref }) : t("pick.noMatch", { ref }) });
          }).catch(() => { feedback("rejected"); setNote({ busy: false, text: t("pick.noMatch", { ref }) }); });
          return;
        }
        feedback("rejected");
        setNote({ busy: false, text: t("pick.noMatch", { ref }) });
      },
    });
  }, [orders, onPick, onFetch]);
  return (
    <>
      <ScanBar inputRef={inputRef} placeholder={t("pick.scanOrder")} />
      {note && (note.busy ? <p className="hint"><b>{note.text}</b></p> : <Banner kind="bad" label={t("banner.rejected")} message={note.text} />)}
    </>
  );
}

interface RunProps {
  registry: Registry;
  settings: ReturnType<typeof getSettings>;
  order: SalesOrderDetail;
  proposal: PickLineProposal[];
  /** Several orders picked together; null for one order. */
  group: PickGroup | null;
  /** The ERP changed the order while picking (re-read and re-proposed). */
  notice: BannerData | null;
  net: { online: boolean; queued: number; stuck: number };
  onReproposal: () => void;
  onDone: (summary: DoneSummary, banner: BannerData | null) => void;
  onAbort: () => void;
}

function Run({ registry, settings, order, proposal, group, notice, net, onReproposal, onDone, onAbort }: RunProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const entryRef = useRef<HTMLInputElement>(null);
  const [run, setRun] = useState<RunState>(() => startRun(FLOW).state);
  const runRef = useRef(run);
  runRef.current = run;
  const [pallet, setPallet] = useState<Pallet | null>(null);
  const [takes, setTakes] = useState<Take[]>([]);
  const takesRef = useRef(takes);
  takesRef.current = takes;
  /** Cartons picked on pallets already closed in this run: they count for the lines. */
  const [earlier, setEarlier] = useState<Take[]>([]);
  const [closedPallets, setClosedPallets] = useState<Pallet[]>([]);
  const [lineNo, setLineNo] = useState<number | null>(null);
  const [view, setView] = useState<"line" | "order">("line");
  const [banner, setBanner] = useState<BannerData | null>(null);
  const [busy, setBusy] = useState(false);
  const [camera, setCamera] = useState(false);
  const [sheet, setSheet] = useState<"abandon" | "manual" | null>(null);
  /** Picking without a scan (owner 2026-11-01): a batch of the article in Trail's stock, with a required comment. */
  const [manualQ, setManualQ] = useState("");
  const [manualNote, setManualNote] = useState("");
  /** Every carton of the article in stock (not only the proposed ones), first-expiry first. */
  const [manualStock, setManualStock] = useState<StockRow[] | null>(null);
  const [otherLot, setOtherLot] = useState<{ container: ContainerSummary; elements: GS1Elements; line: number; proposed: string } | null>(null);
  const [otherReason, setOtherReason] = useState<string | null>(null);
  /** `bulk`: a quantity taken off a loose row of a bulk product (owner 2026-11-03) — typed, never proposed; the row keeps the rest. */
  const [split, setSplit] = useState<{ container: ContainerSummary; elements: GS1Elements; line: number; need: number; have: number; bulk?: boolean; /** The comment of a bulk take started with "Utan scanning". */ manual?: string | null } | null>(null);
  const [splitAmount, setSplitAmount] = useState("");
  const splitCounter = useRef(0);
  /** Zero-point at pick (owner 2026-10-28): the last container Trail knew of left — is the location empty? */
  const [zeroAsk, setZeroAsk] = useState<{ locationId: string; code: string } | null>(null);
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const lines = order.lineItems;
  const prop = (n: number) => proposal.find((p) => p.lineNo === n) ?? null;
  const allTakes = [...earlier, ...takes];
  const pickedFor = (n: number) => allTakes.filter((x) => x.lineNo === n).reduce((s, x) => s + x.quantity, 0);
  const remaining = (n: number) => Math.max(0, num(lines.find((l) => l.lineNo === n)?.quantity ?? "0") - pickedFor(n));
  // Lines in walking order (the proposal's), but products the site puts at the bottom of a
  // pallet first (site setting picking.bottomFirst; owner 2026-10-28: pick order may follow
  // the packaging, heavy and sturdy goods under the rest).
  const bottomWords = registry.bundle.siteConfig?.picking?.bottomFirst ?? [];
  const bottomRank = (l: { productId: string | null }) => {
    const p = l.productId ? registry.bundle.products.find((x) => x.id === l.productId) : undefined;
    return p && isBottomFirst(p, bottomWords) ? 0 : 1;
  };
  const orderedLines = [...lines].sort((a, b) => bottomRank(a) - bottomRank(b) || proposal.findIndex((p) => p.lineNo === a.lineNo) - proposal.findIndex((p) => p.lineNo === b.lineNo));
  const current = lineNo ?? orderedLines.find((l) => remaining(l.lineNo) > 0)?.lineNo ?? orderedLines[0]?.lineNo ?? null;
  const curLine = lines.find((l) => l.lineNo === current) ?? null;
  const curProp = current !== null ? prop(current) : null;
  const curItem = curProp?.items.find((i) => !allTakes.some((x) => x.containerId === i.containerId || x.split?.parentContainerId === i.containerId)) ?? curProp?.items[0] ?? null;
  const step = run.stepId;
  const isPalletStep = step === "pallet";
  const orderComplete = lines.every((l) => remaining(l.lineNo) === 0);

  /** The line the last scan completed, for the camera to close on (owner 2026-10-29). */
  const completedLine = useRef<number | null>(null);
  const record = useCallback((take: Take, elements: GS1Elements, container: ContainerSummary) => {
    const input: ScanInput = { kind: "scan", parsed: elements, resolved: { as: "container", containerId: take.containerId, containerKind: "trade-item", identityKey: container.identity_key } };
    const { state } = applyInput(FLOW, runRef.current, input);
    runRef.current = state;
    setRun(state);
    const line = lines.find((l) => l.lineNo === take.lineNo);
    const got = [...earlier, ...takesRef.current].filter((x) => x.lineNo === take.lineNo).reduce((s, x) => s + x.quantity, 0) + take.quantity;
    if (line && got >= num(line.quantity)) completedLine.current = take.lineNo;
    setTakes((xs) => [...xs, take]);
    feedback("accepted");
    // The customer's carton label (customer-products import): a moment for the automations, only when the customer wants one.
    if (line?.customerLabel?.carton) Api.automationEvent({ type: "carton.picked", flow: "picking", containerId: take.containerId, orderId: origOf(group, take.lineNo)?.id ?? order.id }).catch(() => undefined);
    // Zero-point: when the top-level containers taken from a location reach what stood there, ask.
    const rule = registry.bundle.siteConfig?.counting?.zeroPointAtPick ?? "on";
    if (take.leaves && take.fromLocationId && rule !== "off") {
      const loc = registry.bundle.locations.find((l) => l.id === take.fromLocationId);
      const roots = proposal.flatMap((p) => p.items).find((i) => i.locationId === take.fromLocationId)?.locationRoots;
      const gone = new Set([...earlier, ...takesRef.current, take].filter((x) => x.fromLocationId === take.fromLocationId && x.leaves).map((x) => x.leaves)).size;
      if (roots !== undefined && gone >= roots && (rule === "on" || loc?.roles.includes("storage"))) setZeroAsk({ locationId: take.fromLocationId, code: loc?.code ?? "?" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lines, earlier, proposal, registry]);
  // The last line done: show the whole order with the way out (owner 2026-10-29: "nothing happens").
  useEffect(() => {
    if (step !== "items" || lines.length === 0) return;
    const complete = lines.every((l) => Math.max(0, num(l.quantity) - allTakes.filter((x) => x.lineNo === l.lineNo).reduce((s, x) => s + x.quantity, 0)) === 0);
    if (complete && takes.length > 0) {
      setView("order");
      setBanner({ kind: "ok", label: t("pick.allPicked"), message: t("pick.allPickedMsg") });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takes.length, earlier.length, step]);

  /** Which line a carton belongs to: the shown line when its product matches, else the first open line with that product. */
  const lineFor = useCallback((productId: string | null) => {
    if (!productId) return null;
    if (curLine && curLine.productId === productId && remaining(curLine.lineNo) > 0) return curLine.lineNo;
    return orderedLines.find((l) => l.productId === productId && remaining(l.lineNo) > 0)?.lineNo ?? null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curLine, orderedLines, allTakes]);

  const takeCarton = useCallback((c: ContainerSummary, elements: GS1Elements, n: number, overrideReason: string | null, fromPalletId: string | null, manual: string | null = null) => {
    const need = remaining(n);
    const have = num(c.quantity);
    if (have > need && need > 0) {
      setSplit({ container: c, elements, line: n, need, have });
      setSplitAmount(str(need));
      return;
    }
    record({ containerId: c.container_id, lineNo: n, lot: c.lot_number, quantity: have, uom: c.uom, overrideReason, manual, split: null, fromPalletId, palletId: null, at: new Date().toISOString(), fromLocationId: c.location_id, leaves: fromPalletId ? null : c.container_id }, elements, c);
    setBanner(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record, allTakes, lines]);

  /** A carton the proposal already names is accepted without asking the server (owner
   *  2026-10-29: the wait after a scan); anything else is looked up. */
  const resolve = useCallback(async (elements: GS1Elements): Promise<ContainerSummary[] | null> => {
    const key = elements.sscc ? `sscc:00=${elements.sscc}` : identityKey(SCHEME, elements);
    if (!key) return null;
    for (const p of proposal) {
      const i = p.items.find((x) => x.identityKey === key);
      if (i) return [{ container_id: i.containerId, kind: "trade-item", identity_key: i.identityKey, product_id: p.productId, gtin: i.gtin, lot_number: i.lotNumber, sscc: null, quantity: i.quantity, uom: i.uom, parent_id: i.parentId, location_id: i.locationId, state: "stored", children: 0 }];
    }
    try {
      return await Api.containersByIdentity(key);
    } catch (e) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
      return null;
    }
  }, [proposal]);

  /** The manual pick: the chosen carton as if it had been scanned, the comment as the override reason. */
  const manualPick = async (containerId: string) => {
    if (!curLine || !manualNote.trim()) { feedback("rejected"); return; }
    try {
      const c = await Api.container(containerId);
      const el: GS1Elements = { ...plainElements("", null), gtin: c.gtin, lot: c.lot_number, ai: { ...(c.gtin ? { "01": c.gtin } : {}), ...(c.lot_number ? { "10": c.lot_number } : {}) } };
      setSheet(null);
      takeCarton(c, el, curLine.lineNo, t("pick.manualReason", { note: manualNote.trim() }), c.parent_id ?? null, manualNote.trim());
    } catch (e) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) }); }
  };
  /** A bulk product (owner 2026-11-03): no label to scan, so the way in is the product's barcode or its
   *  article number typed in the scan field, and then the place — the article may stand in several
   *  (owner: "man måste ju även ange en lagerplats"). One place: straight to the quantity. Several: a list
   *  to pick from, the proposed place first; or the picker scans the place's label instead. The row keeps
   *  what is not taken. Offline, the proposal's rows stand in for the stock list. */
  const [bulkWhere, setBulkWhere] = useState<{ product: BundleProduct; elements: GS1Elements; manual: string | null; places: { locationId: string; code: string; quantity: number; uom: string; row: StockRow; proposed: boolean }[] } | null>(null);
  const takenFrom = useCallback((id: string) => allTakes.filter((x) => x.containerId === id || x.split?.parentContainerId === id).reduce((s, x) => s + x.quantity, 0), [allTakes]);
  const startBulkAt = useCallback((product: BundleProduct, elements: GS1Elements, n: number, row: { container_id: string; kind: string; identity_key?: string | null; gtin: string | null; lot_number: string | null; quantity: string; uom: string; parent_id: string | null; location_id: string | null }, manual: string | null = null) => {
    const left = num(row.quantity) - takenFrom(row.container_id);
    const c: ContainerSummary = { container_id: row.container_id, kind: row.kind, identity_key: row.identity_key ?? null, product_id: product.id, gtin: row.gtin, lot_number: row.lot_number, sscc: null, quantity: str(left), uom: row.uom, parent_id: row.parent_id, location_id: row.location_id, state: "stored", children: 0 };
    setBulkWhere(null);
    feedback("attention");
    setSplit({ container: c, elements, line: n, need: remaining(n), have: left, bulk: true, manual });
    setSplitAmount("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [takenFrom, allTakes, lines]);
  const startBulk = useCallback(async (product: BundleProduct, elements: GS1Elements, locationId: string | null = null, manual: string | null = null): Promise<CodeState> => {
    const n = lineFor(product.id);
    if (n === null) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: lines.some((l) => l.productId === product.id) ? t("pick.lineFull") : t("pick.notOnOrder") }); return "rejected"; }
    const proposedAt = new Set((prop(n)?.items ?? []).map((i) => i.locationId).filter((x): x is string => !!x));
    let rows: StockRow[] | null = null;
    try { rows = await Api.stock(product.id); } catch { rows = null; }
    // What can be taken: stock rows of the product with something left, not on an order pallet.
    const usable = (rows ?? (prop(n)?.items ?? []).map((i) => ({ container_id: i.containerId, kind: i.kind, identity_key: i.identityKey, gtin: i.gtin, lot_number: i.lotNumber, quantity: i.quantity, uom: i.uom, parent_id: i.parentId, state: "stored", location_id: i.locationId, location_code: i.code, location_path: i.path, sscc: null, intact: true, sku: product.sku, product_name: product.name, children: 0, best_before: i.bestBefore } as StockRow)))
      .filter((r) => r.kind !== "logistic-unit" && (r.state === "received" || r.state === "stored") && r.location_id && num(r.quantity) - takenFrom(r.container_id) > 0);
    const here = locationId ? usable.filter((r) => r.location_id === locationId) : usable;
    if (here.length === 0) {
      const code = locationId ? registry.bundle.locations.find((l) => l.id === locationId)?.code ?? "?" : null;
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: code ? t("pick.bulkNoStockAt", { what: product.name, code }) : t("pick.bulkNoStock", { what: product.name }) });
      return "rejected";
    }
    // One place (or the place was given): the quantity. Several: choose, the proposed place first.
    const byPlace = new Map<string, typeof here>();
    for (const r of here) byPlace.set(r.location_id!, [...(byPlace.get(r.location_id!) ?? []), r]);
    if (byPlace.size === 1) { startBulkAt(product, elements, n, here[0]!, manual); return "accepted"; }
    const places = [...byPlace.entries()].map(([lid, rs]) => ({ locationId: lid, code: rs[0]!.location_code ?? registry.bundle.locations.find((l) => l.id === lid)?.code ?? "?", quantity: rs.reduce((s, r) => s + num(r.quantity) - takenFrom(r.container_id), 0), uom: rs[0]!.uom, row: rs[0]!, proposed: proposedAt.has(lid) }))
      .sort((a, b) => Number(b.proposed) - Number(a.proposed) || a.code.localeCompare(b.code, "sv", { numeric: true }));
    feedback("attention");
    setBulkWhere({ product, elements, manual, places });
    return "accepted";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineFor, lines, proposal, registry, takenFrom, startBulkAt]);

  const handleRawInner = useCallback(
    async (data: string): Promise<CodeState> => {
      const before = runRef.current;
      const aim = /^\]\w\w/.exec(data)?.[0] ?? null;
      let elements: GS1Elements | null = null;
      if (!(aim && !isGs1Symbology(data))) {
        try { elements = await parseScan(data); } catch { elements = null; }
      }
      if (!elements || (!elements.sscc && !elements.gtin)) {
        // An article number typed in the field (owner 2026-11-03): for a bulk product that is the way in,
        // no label to scan; a batch-handled article still needs its carton's code.
        const typed = data.trim().toLowerCase();
        // The place's label, for a bulk line: take from there (owner 2026-11-03: the article may stand in several places).
        const loc = before.stepId !== "pallet" ? findLocation(registry, data) : null;
        const lineProduct = curLine?.productId ? registry.bundle.products.find((p) => p.id === curLine.productId) ?? null : null;
        if (loc && lineProduct && isBulkProduct(registry, lineProduct)) {
          const gtin = lineProduct.gtins[0]?.gtin ?? null;
          return startBulk(lineProduct, { ...plainElements(data, aim), gtin, ai: gtin ? { "01": gtin } : {} }, loc.id);
        }
        // The article number as typed, or without the leading zeros Visma pads it with ("5325503" for 0005325503).
        const bare = typed.replace(/^0+/, "");
        const bySku = typed && before.stepId !== "pallet"
          ? registry.bundle.products.find((p) => p.sku.toLowerCase() === typed) ?? (bare ? registry.bundle.products.filter((p) => p.sku.toLowerCase().replace(/^0+/, "") === bare) : []).reduce<BundleProduct | null>((one, p, _i, all) => (all.length === 1 ? p : one), null)
          : null;
        if (bySku && isBulkProduct(registry, bySku)) {
          const gtin = bySku.gtins[0]?.gtin ?? null;
          return startBulk(bySku, { ...plainElements(data, aim), gtin, ai: gtin ? { "01": gtin } : {} });
        }
        if (bySku) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.skuNeedsLabel", { sku: bySku.sku }) }); return "rejected"; }
        // The number is on the order but the article is not in the register the phone carries: inactive.
        const onLine = typed ? lines.find((l) => (l.sku ?? "").toLowerCase().replace(/^0+/, "") === bare) ?? null : null;
        if (onLine) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.skuInactive", { sku: onLine.sku ?? data.trim() }) }); return "rejected"; }
        feedback("rejected");
        // In the pallet step nothing but the order's pallet is wanted: say so, not "unknown code".
        setBanner({ kind: "bad", label: t("banner.rejected"), message: before.stepId === "pallet" ? t("pick.scanPallet") : t("msg.unknownCode", { raw: data.slice(0, 40) }) });
        return "rejected";
      }
      setBusy(true);
      const rows = await resolve(elements);
      setBusy(false);
      if (before.stepId === "pallet") {
        if (!elements.sscc) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.scanPallet") }); return "rejected"; }
        const existing = rows?.find((r) => r.kind === "logistic-unit") ?? null;
        // A stock pallet with goods on it as the order's pallet (owner 2026-11-02: orders for whole
        // pallets): allowed only when everything on it is for this order — then every carton is
        // picked at once. Anything else on it refuses the scan: the pallet would leave with it.
        const whole: { k: ContainerSummary; n: number; kel: GS1Elements }[] = [];
        if (existing && (existing.state === "received" || existing.state === "stored") && existing.children > 0) {
          const full = await Api.container(existing.container_id).catch(() => null);
          const kids = full?.children ?? [];
          const assigned = new Map<number, number>();
          const foreign: string[] = [];
          // The right article but more than the order needs (owner 2026-11-02: two 7336 m cartons against 9600 m) is its own answer.
          const over: { name: string; have: number; need: number; uom: string }[] = [];
          for (const k of kids) {
            const qty = num(k.quantity);
            const line = orderedLines.find((l) => l.productId !== null && l.productId === k.product_id && remaining(l.lineNo) - (assigned.get(l.lineNo) ?? 0) >= qty);
            if (!line) {
              const name = registry.bundle.products.find((x) => x.id === k.product_id)?.name ?? k.lot_number ?? k.gtin ?? "?";
              const onOrder = orderedLines.find((l) => l.productId !== null && l.productId === k.product_id);
              if (onOrder) over.push({ name, have: qty, need: Math.max(0, remaining(onOrder.lineNo) - (assigned.get(onOrder.lineNo) ?? 0)), uom: k.uom });
              else foreign.push(name);
              continue;
            }
            assigned.set(line.lineNo, (assigned.get(line.lineNo) ?? 0) + qty);
            whole.push({ k, n: line.lineNo, kel: { ...elements, sscc: null, gtin: k.gtin, lot: k.lot_number, ai: { ...(k.gtin ? { "01": k.gtin } : {}), ...(k.lot_number ? { "10": k.lot_number } : {}) } } });
          }
          if (foreign.length > 0) {
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.palletForeign", { sscc: `…${elements.sscc.slice(-5)}`, n: foreign.length, what: [...new Set(foreign)].slice(0, 3).join(", ") }) });
            return "rejected";
          }
          if (over.length > 0) {
            const o = over[0]!;
            feedback("rejected");
            setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.palletOver", { sscc: `…${elements.sscc.slice(-5)}`, n: over.length, what: o.name, have: fmtQty(String(o.have), o.uom), need: fmtQty(String(o.need), o.uom) }) });
            return "rejected";
          }
        }
        const id = existing?.container_id ?? uuidv7();
        const p: Pallet = { containerId: id, sscc: elements.sscc, fresh: !existing || existing.state === "received" || existing.state === "stored" ? !existing : false };
        setPallet(p);
        const input: ScanInput = { kind: "scan", parsed: elements, resolved: { as: "container", containerId: id, containerKind: "logistic-unit", identityKey: `sscc:00=${elements.sscc}` } };
        const { state } = applyInput(FLOW, before, input);
        runRef.current = state;
        setRun(state);
        for (const w of whole) record({ containerId: w.k.container_id, lineNo: w.n, lot: w.k.lot_number, quantity: num(w.k.quantity), uom: w.k.uom, overrideReason: null, manual: null, split: null, fromPalletId: null, palletId: null, at: new Date().toISOString(), fromLocationId: existing?.location_id ?? null, leaves: existing?.container_id ?? null }, w.kel, w.k);
        feedback(whole.length ? "attention" : "accepted");
        setBanner(whole.length ? { kind: "ok", label: t("banner.accepted"), message: t("pick.palletWhole", { sscc: `…${elements.sscc.slice(-5)}`, n: whole.length }) } : null);
        return "accepted";
      }
      // A bulk product (owner 2026-11-03): the barcode names the product, not a carton.
      if ((!rows || rows.length === 0) && elements.gtin) {
        const hit = registry.byGtin.get(elements.gtin) ?? null;
        if (hit && isBulkProduct(registry, hit.product)) return startBulk(hit.product, elements);
      }
      if (!rows || rows.length === 0) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.notFound") }); return "rejected"; }
      if (rows.length > 1) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("putaway.several", { n: rows.length }) }); return "rejected"; }
      const c = rows[0]!;
      if (c.kind === "logistic-unit" && pallet && pallet.containerId === c.container_id) {
        // The order's own pallet scanned again in the carton step: nothing to take from it.
        feedback("rejected");
        setBanner({ kind: "warn", label: t("pick.isOrderPallet"), message: t("pick.isOrderPallet.msg") });
        return "rejected";
      }
      if (c.kind === "logistic-unit") {
        // A stock pallet scanned whole: every carton on it, line by line.
        const full = await Api.container(c.container_id).catch(() => null);
        const kids = full?.children ?? [];
        let taken = 0;
        for (const k of kids) {
          if (allTakes.some((x) => x.containerId === k.container_id)) continue;
          const n = lineFor(k.product_id);
          if (n === null) continue;
          const kel: GS1Elements = { ...elements, sscc: null, gtin: k.gtin, lot: k.lot_number, ai: { ...(k.gtin ? { "01": k.gtin } : {}), ...(k.lot_number ? { "10": k.lot_number } : {}) } };
          const fromPallet = pallet && pallet.containerId === c.container_id ? null : c.container_id;
          const need = remaining(n);
          if (num(k.quantity) > need) break;
          record({ containerId: k.container_id, lineNo: n, lot: k.lot_number, quantity: num(k.quantity), uom: k.uom, overrideReason: null, manual: null, split: null, fromPalletId: fromPallet, palletId: null, at: new Date().toISOString(), fromLocationId: c.location_id, leaves: fromPallet ? null : c.container_id }, kel, k);
          taken += 1;
        }
        if (taken === 0) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.notOnOrder") }); return "rejected"; }
        setBanner({ kind: "ok", label: t("banner.accepted"), message: t("pick.wholePallet", { n: taken }) });
        return "accepted";
      }
      if (c.state === "picked" || allTakes.some((x) => x.containerId === c.container_id)) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.alreadyPicked") }); return "rejected"; }
      const n = lineFor(c.product_id);
      if (n === null) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: lines.some((l) => l.productId === c.product_id) ? t("pick.lineFull") : t("pick.notOnOrder") }); return "rejected"; }
      const p = prop(n);
      const minDays = order.minShelfLifeDays;
      const cand = p?.items.find((i) => i.containerId === c.container_id) ?? null;
      if (minDays != null && cand?.bestBefore && new Date(cand.bestBefore).getTime() < Date.now() + minDays * 86_400_000) {
        feedback("rejected");
        setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.tooShort", { date: cand.bestBefore, days: minDays }) });
        return "rejected";
      }
      const proposedLot = p?.items.find((i) => !allTakes.some((x) => x.containerId === i.containerId))?.lotNumber ?? null;
      if (proposedLot && c.lot_number && c.lot_number !== proposedLot && !cand) {
        feedback("attention");
        setOtherLot({ container: c, elements, line: n, proposed: proposedLot });
        setOtherReason(null);
        return "accepted";
      }
      takeCarton(c, elements, n, null, c.parent_id && (!pallet || c.parent_id !== pallet.containerId) ? c.parent_id : null);
      return "accepted";
    },
    [resolve, lineFor, takeCarton, record, allTakes, lines, orderedLines, order.minShelfLifeDays, pallet, proposal, registry, startBulk, curLine],
  );
  /** Wedge, camera and typed input share one pipeline; the camera also gets the verdict for its overlay. */
  const handleRaw = useCallback(
    async (data: string, mark?: (d: string, s: CodeState) => void): Promise<CodeState> => {
      const st = await handleRawInner(data);
      mark?.(data, st);
      return st;
    },
    [handleRawInner],
  );

  // Camera: colour the code in the picture, and when a scan fills the line close the camera
  // and go on to the next open line (the whole-order view takes over when all are done).
  const onCameraCode = useCallback(async (data: string, _format: string, mark: (d: string, s: CodeState) => void) => {
    completedLine.current = null;
    const st = await handleRaw(data, mark);
    if (st === "accepted" && runRef.current.stepId === "pallet") setCamera(false);
    if (st === "accepted" && completedLine.current !== null) {
      const done = completedLine.current;
      const next = orderedLines.find((l) => l.lineNo !== done && Math.max(0, num(l.quantity) - [...earlier, ...takesRef.current].filter((x) => x.lineNo === l.lineNo).reduce((s, x) => s + x.quantity, 0)) > 0);
      setCamera(false);
      if (next) { setLineNo(next.lineNo); setView("line"); }
    }
    return st;
  }, [handleRaw, orderedLines, earlier]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el || camera || sheet || otherLot || split) return;
    focusScan(el);
    return attachWedge(el, { onScan: (d) => void handleRaw(d) });
  }, [handleRaw, camera, sheet, otherLot, split]);
  useEffect(() => {
    if (!camera && !sheet && !otherLot && !split && !zeroAsk && !bulkWhere) focusScan(inputRef.current);
  }, [step, camera, sheet, otherLot, split, view, zeroAsk]);

  /** The zero-point answer: "yes" is a Counted observation (empty, no review); "no" puts the
   *  location in today's zero-point round — a count mid-pallet would lose the pallet's scans. */
  const zeroAnswer = async (empty: boolean) => {
    if (!zeroAsk) return;
    const ask = zeroAsk;
    setZeroAsk(null);
    if (empty) {
      const ev: EventEnvelope = { eventId: uuidv7(), tenantId: settings.tenantId, goodsOwnerId: registry.bundle.goodsOwner.id, type: "counted", class: classOf("counted"), schemaVersion: 1, occurredAt: new Date().toISOString(), actorId: settings.actorId, deviceId: settings.deviceId, capture: "system", captureDetail: "zero-point", readPoint: ask.locationId, bizLocation: ask.locationId, flowId: FLOW.id, flowVersion: FLOW.version, batchId: uuidv7(), payload: { locationId: ask.locationId, containerIds: [], missingContainerIds: [], quantities: [], roundId: null, empty: true } };
      let result: "stored" | "queued" = "queued";
      try { result = await submitStep({ flowId: FLOW.id, flowVersion: FLOW.version, stepId: "items", events: [ev], effects: [] }); } catch { result = "queued"; }
      setBanner({ kind: "ok", label: t("banner.accepted"), message: result === "stored" ? t("pick.zeroDone", { code: ask.code }) : t("pick.zeroQueued", { code: ask.code }) });
      return;
    }
    try {
      const r = await Api.zeroPointCount(ask.locationId);
      setBanner({ kind: "warn", label: t("pick.zeroNo"), message: t("pick.zeroNoDone", { code: ask.code, name: r.name }) });
    } catch (e) {
      setBanner({ kind: "bad", label: t("banner.rejected"), message: e instanceof Error ? e.message : String(e) });
    }
  };

  const newPallet = async () => {
    try {
      const { sscc } = await Api.issueSscc();
      await handleRaw(`(00)${sscc}`);
      Api.automationEvent({ type: "pallet.started", flow: "picking", sscc, orderId: order.id }).catch(() => undefined);
    } catch (e) {
      feedback("rejected");
      setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.issueFailed", { err: e instanceof Error ? e.message : String(e) }) });
    }
  };

  const confirmSplit = () => {
    if (!split) return;
    const take = Number(splitAmount.replace(",", "."));
    if (!(take > 0)) return;
    if (take > split.have) { feedback("rejected"); setBanner({ kind: "bad", label: t("banner.rejected"), message: t("pick.bulkTooMuch", { have: str(split.have), uom: uomLabel(split.container.uom) }) }); return; }
    const c = split.container;
    if (split.bulk) {
      // Bulk (owner 2026-11-03): no identity for the piece, no label, no lot. The whole row when all of it goes.
      const code = c.location_id ? registry.bundle.locations.find((l) => l.id === c.location_id)?.code ?? "?" : "?";
      const manual = split.manual ?? null;
      const why = manual ? t("pick.manualReason", { note: manual }) : null;
      if (take === split.have) record({ containerId: c.container_id, lineNo: split.line, lot: null, quantity: take, uom: c.uom, overrideReason: why, manual, split: null, fromPalletId: c.parent_id, palletId: null, at: new Date().toISOString(), fromLocationId: c.location_id, leaves: c.parent_id ? null : c.container_id }, split.elements, c);
      else record({ containerId: uuidv7(), lineNo: split.line, lot: null, quantity: take, uom: c.uom, overrideReason: why, manual, split: { parentContainerId: c.container_id, identity: { key: null, ai: c.gtin ? { "01": c.gtin } : {} }, remaining: split.have - take }, fromPalletId: null, palletId: null, at: new Date().toISOString(), fromLocationId: c.location_id, leaves: null }, split.elements, c);
      setBanner({ kind: "ok", label: t("banner.accepted"), message: t("pick.bulkNote", { q: str(take), uom: uomLabel(c.uom), code, remaining: str(split.have - take) }) });
      setSplit(null);
      return;
    }
    const mode = registry.bundle.siteConfig?.picking?.splitIdentity ?? "lot-suffix";
    splitCounter.current += 1;
    const lot = c.lot_number ?? "";
    const ai: Record<string, string> = { ...(c.gtin ? { "01": c.gtin } : {}) };
    let key: string;
    let childLot = lot;
    if (mode === "serial") {
      const serial = String(splitCounter.current).padStart(4, "0");
      ai["10"] = lot;
      ai["21"] = serial;
      key = `gtin+lot+serial:01=${c.gtin ?? ""};10=${lot};21=${serial}`;
    } else {
      childLot = `${lot}-${splitCounter.current}`;
      ai["10"] = childLot;
      key = `gtin+lot:01=${c.gtin ?? ""};10=${childLot}`;
    }
    const childId = uuidv7();
    const elements: GS1Elements = { ...split.elements, lot: childLot, ai: { ...split.elements.ai, ...ai } };
    record({ containerId: childId, lineNo: split.line, lot: childLot, quantity: take, uom: c.uom, overrideReason: null, manual: null, split: { parentContainerId: c.container_id, identity: { key, ai }, remaining: split.have - take }, fromPalletId: null, palletId: null, at: new Date().toISOString(), fromLocationId: c.location_id, leaves: null }, elements, { ...c, container_id: childId, identity_key: key, lot_number: childLot, quantity: str(take) });
    // The piece's label (mockup picking-v1 §4): the phone knows the identity before the ledger does.
    const product = c.product_id ? registry.bundle.products.find((p) => p.id === c.product_id) : undefined;
    Api.automationEvent({ type: "piece.split", flow: "picking", orderId: order.id, piece: { gtin: c.gtin, sku: product?.sku ?? c.gtin ?? "?", name: product?.name ?? "", lot: childLot, serial: mode === "serial" ? ai["21"] ?? null : null, parentLot: lot || null, quantity: str(take), uom: c.uom, reference: order.reference } }).catch(() => undefined);
    setBanner({ kind: "ok", label: t("banner.accepted"), message: t("pick.splitNote", { lot: childLot, remaining: str(split.have - take), uom: c.uom }) });
    setSplit(null);
  };

  const closePallet = async () => {
    if (!pallet) return;
    const { state } = applyInput(FLOW, runRef.current, { kind: "terminate" });
    setBusy(true);
    const batch = buildPickBatch(state, pallet, takesRef.current, { settings, goodsOwnerId: registry.bundle.goodsOwner.id, orderId: order.id, orderIdOf: (n) => origOf(group, n)?.id ?? order.id, lineNoOf: (n) => origOf(group, n)?.lineNo ?? n, products: new Map(registry.bundle.products.map((p) => [p.id, p])), outboundLocationId: registry.bundle.siteConfig?.picking?.outboundLocationId ?? null });
    let result: "stored" | "queued" = "queued";
    // `reused`: the pallet existed before this run (stock pallet picked whole, or resumed) and has its label (owner 2026-11-02).
    const closedEvent: AutomationEventIn = { type: "pallet.closed", flow: "picking", containerId: pallet.containerId, orderId: order.id, reused: !pallet.fresh };
    const all = [...earlier, ...takesRef.current.map((x) => ({ ...x, palletId: pallet.containerId }))];
    // The pallet that completes the order: its label waits for the ERP's verdict (owner 2026-11-03: nothing
    // should print when the ERP refuses the pick). Queued offline, the queue fires it as before.
    const completes = lines.every((l) => num(l.quantity) - all.filter((x) => x.lineNo === l.lineNo).reduce((s, x) => s + x.quantity, 0) <= 0);
    try { result = await submitStep(batch, completes ? undefined : [closedEvent]); } catch { result = "queued"; }
    setBusy(false);
    // "Pall klar" is a moment for the automations (spec §6.8); a label needs the pallet stored first. Queued: the queue fires it when the step goes through.
    if (result === "stored" && !completes) Api.automationEvent(closedEvent).catch(() => undefined);
    const closed = [...closedPallets, pallet];
    setClosedPallets(closed);
    setEarlier(all);
    setTakes([]);
    setPallet(null);
    const banner: BannerData = result === "stored" ? { kind: "ok", label: t("pick.palletSaved"), message: t("pick.palletSavedMsg", { n: takesRef.current.length, sscc: shortSscc(pallet.sscc) }) } : { kind: "warn", label: t("pick.palletQueued"), message: t("pick.palletSavedMsg", { n: takesRef.current.length, sscc: shortSscc(pallet.sscc) }) };
    if (completes) {
      await finish(closed, all, banner, null, closedEvent);
      return;
    }
    const fresh = startRun(FLOW).state;
    runRef.current = fresh;
    setRun(fresh);
    setBanner(banner);
    onReproposal();
  };

  /** Lines still open when the order is closed: the picker gives a reason per line before the
   *  order is reported (mockup picking-v1/shortages). */
  const [shortStep, setShortStep] = useState<{ pallets: Pallet[]; all: Take[]; banner: BannerData | null; lastPalletEvent?: AutomationEventIn | null } | null>(null);
  const [shortReasons, setShortReasons] = useState<Record<number, string>>({});
  const shortLines = (all: Take[]) => lines.filter((l) => num(l.quantity) - all.filter((x) => x.lineNo === l.lineNo).reduce((s, x) => s + x.quantity, 0) > 0);
  const shortReasonOptions = reasonsForShort(registry.bundle.siteConfig ?? null);
  const reasonLabel = (code: string | null) => (code ? shortReasonOptions.find((r) => r.value === code)?.label ?? code : null);

  const finish = async (pallets: Pallet[], all: Take[], banner: BannerData | null, reasons: Record<number, string> | null = null, lastPalletEvent: AutomationEventIn | null = null) => {
    const short = shortLines(all);
    if (short.length > 0 && reasons === null) {
      // Ask for the reasons first; the report follows from the shortage step.
      setShortStep({ pallets, all, banner, lastPalletEvent });
      setShortReasons({});
      return;
    }
    // One report per order (several when picked together): its own line numbers, the pallets that
    // carry its cartons, and which other orders share each pallet.
    const members = group ? group.orders : [{ id: order.id, reference: order.reference, offset: 0, detail: order }];
    const memberOf = (lineNo: number) => (group ? origOf(group, lineNo)?.id ?? order.id : order.id);
    const lineReport = (l: typeof lines[number]) => {
      const mine = all.filter((x) => x.lineNo === l.lineNo);
      const got = mine.reduce((s, x) => s + x.quantity, 0);
      const isShort = got < num(l.quantity);
      return { got, isShort, report: { lineNo: origOf(group, l.lineNo)?.lineNo ?? l.lineNo, containerIds: mine.map((x) => x.containerId), lots: [...new Set(mine.map((x) => x.lot).filter((x): x is string => x !== null))], quantity: str(got), short: isShort ? str(num(l.quantity) - got) : null, reason: isShort ? (reasons?.[l.lineNo] ?? null) : null } };
    };
    let b = banner;
    let verdict: "sent" | "unknown" = "unknown";
    let refused: { verdict: string; message: string } | null = null;
    for (const m of members) {
      const myLines = lines.filter((l) => memberOf(l.lineNo) === m.id);
      const mine = all.filter((x) => memberOf(x.lineNo) === m.id);
      const report = {
        pallets: pallets.filter((p) => mine.some((x) => x.palletId === p.containerId)).map((p) => ({
          containerId: p.containerId, sscc: p.sscc, items: mine.filter((x) => x.palletId === p.containerId).map((x) => x.containerId),
          sharedWith: members.filter((o) => o.id !== m.id && all.some((x) => x.palletId === p.containerId && memberOf(x.lineNo) === o.id)).map((o) => o.reference),
        })),
        lines: myLines.map((l) => lineReport(l).report),
      };
      try {
        // Wait up to ten seconds for the ERP's verdict (owner 2026-11-03); the server refuses the close when the ERP refuses.
        const r = await Api.reportPicked(m.id, { ...report, wait: navigator.onLine ? 10 : 0 });
        if (r.verdict === "sent") verdict = "sent";
      } catch (e) {
        if (e instanceof ApiError && e.status === 409 && (e.body as { error?: string } | null)?.error === "erp_refused") { refused = { verdict: (e.body as { verdict?: string }).verdict ?? "refused", message: (e.body as { message?: string }).message ?? "" }; break; }
        b = { kind: "bad", label: t("banner.rejected"), message: t("pick.reportFailed", { err: e instanceof Error ? e.message : String(e) }) };
      }
    }
    setShortStep(null);
    if (refused) {
      // The order stays as picking with everything taken; the picker sorts it out in the ERP and tries again.
      feedback("rejected");
      onDoneRef.current({ at: new Date().toISOString(), pallets: pallets.length, kolli: all.length, short: lines.filter((l) => lineReport(l).isShort).length, orders: members.map((m) => m.reference), lines: [], refused: { ...refused, retry: () => { void finish(pallets, all, banner, reasons, lastPalletEvent); } } }, null);
      return;
    }
    // The last pallet's label, held until now.
    if (lastPalletEvent) Api.automationEvent(lastPalletEvent).catch(() => undefined);
    onDoneRef.current({
      at: new Date(Date.now() - 5000).toISOString(),
      verdict,
      pallets: pallets.length,
      kolli: all.length,
      short: lines.filter((l) => lineReport(l).isShort).length,
      orders: members.map((m) => m.reference),
      lines: lines.map((l) => {
        const r = lineReport(l);
        return { name: l.productName ?? l.description ?? l.sku ?? "", sku: l.sku ?? null, got: r.got, ordered: num(l.quantity), uom: l.uom, short: r.report.short ? num(r.report.short) : 0, reason: reasonLabel(r.report.reason), orderRef: group ? origOf(group, l.lineNo)?.reference ?? null : null };
      }),
    }, b);
  };

  const finishShort = async () => {
    // "Order plockad" with lines still open: an open pallet with kolli on it is closed first;
    // otherwise the order is reported, through the shortage step.
    if (pallet && takesRef.current.length > 0) return closePallet();
    await finish(closedPallets, earlier, null);
  };

  if (camera) return <CameraView onCode={onCameraCode} onClose={() => setCamera(false)} />;

  const reasons = (registry.bundle.siteConfig?.reasons?.override ?? []).map((r) => ({ value: r.code, label: r.label }));
  const palletIndex = closedPallets.length + 1;
  const kolliOnPallet = takes.length;
  const lineIndex = current !== null ? orderedLines.findIndex((l) => l.lineNo === current) + 1 : 0;

  if (shortStep) {
    const short = shortLines(shortStep.all);
    const allGiven = short.every((l) => shortReasons[l.lineNo]);
    return (
      <div className="screen">
        <Strip left={<b>{t("menu.pick")}</b>} middle={`${order.reference} · ${order.customer?.name ?? ""}`} {...net} />
        <div className="body">
          <Prompt step={t("pick.shortStep")} title={short.length === 1 ? t("pick.shortTitleOne") : t("pick.shortTitle", { n: short.length })} sub={t("pick.shortSub")} />
          {short.map((l) => {
            const got = shortStep.all.filter((x) => x.lineNo === l.lineNo).reduce((s, x) => s + x.quantity, 0);
            return (
              <div key={l.lineNo}>
                <Rows rows={[{ k: l.productName ?? l.description ?? l.sku ?? "", small: [l.sku, `${fmtNum(got)} / ${fmtQty(l.quantity, l.uom)}`].filter(Boolean).join(" · "), q: t("pick.short", { n: fmtQty(num(l.quantity) - got, l.uom) }), tone: "warn" }]} />
                <Chips options={shortReasonOptions} value={(shortReasons[l.lineNo] as typeof shortReasonOptions[number]["value"] | undefined) ?? null} onChange={(v) => setShortReasons({ ...shortReasons, [l.lineNo]: v })} />
              </div>
            );
          })}
        </div>
        <Actions>
          <Btn kind="quiet" icon="back" grow={false} onClick={() => setShortStep(null)} />
          <Btn kind="primary" disabled={!allGiven || busy} onClick={() => { setBusy(true); void finish(shortStep.pallets, shortStep.all, shortStep.banner, shortReasons, shortStep.lastPalletEvent ?? null).finally(() => setBusy(false)); }}>{t("pick.report")}</Btn>
        </Actions>
      </div>
    );
  }

  return (
    <div className="screen" onClick={() => { unlockAudio(); if (split) entryRef.current?.focus(); else focusScan(inputRef.current); }}>
      <Strip left={<b>{t("menu.pick")}</b>} middle={pallet ? `${order.reference} · ${t("unit.pallet")} ${shortSscc(pallet.sscc)}` : `${order.reference} · ${order.customer?.name ?? ""}`} {...net} />
      <div className="body">
        {notice && !otherLot && !split && <Banner {...notice} />}
        {banner && !otherLot && !split && <Banner {...banner} />}
        {isPalletStep && (
          <>
            <Prompt step={t("pick.step2")} title={t("pick.title2")} sub={t("pick.sub2")} />
            <ScanBar inputRef={inputRef} placeholder={t("pick.scanPallet")} onCamera={() => { unlockAudio(); setCamera(true); }} />
            <div className="label"><div className="empty">{closedPallets.length === 0 ? t("pick.noPallet", { ref: order.reference, lines: lines.length }) : t("pick.palletSavedMsg", { n: earlier.length, sscc: shortSscc(closedPallets[closedPallets.length - 1]!.sscc) })}</div></div>
          </>
        )}
        {!isPalletStep && split && (
          <>
            <Prompt step={t("pick.step3", { n: lineIndex, total: lines.length, p: palletIndex })} title={split.bulk ? t("pick.bulkTitle") : t("pick.splitTitle")} sub={split.bulk ? t("pick.bulkSub", { have: str(split.have), uom: uomLabel(split.container.uom), need: str(split.need), code: split.container.location_id ? registry.bundle.locations.find((l) => l.id === split.container.location_id)?.code ?? "?" : "?" }) : t("pick.splitSub", { have: str(split.have), uom: uomLabel(split.container.uom), need: str(split.need) })} />
            <Entry confirm={{ label: t("pick.splitDo"), disabled: !(Number(splitAmount.replace(",", ".")) > 0) }} value={splitAmount} onChange={setSplitAmount} onEnter={confirmSplit} inputMode="numeric" placeholder={split.bulk ? t("pick.bulkPlaceholder") : str(split.need)} tag={uomLabel(split.container.uom)} inputRef={entryRef} />
            <Chips options={[{ value: "need", label: t("pick.splitTake", { n: str(Math.min(split.need, split.have)), uom: uomLabel(split.container.uom) }) }, { value: "whole", label: split.bulk ? t("pick.bulkWhole") : t("pick.splitWhole") }]} value={splitAmount === str(split.need) ? "need" : splitAmount === str(split.have) ? "whole" : null} onChange={(v) => setSplitAmount(v === "need" ? str(Math.min(split.need, split.have)) : str(split.have))} />
          </>
        )}
        {!isPalletStep && otherLot && (
          <>
            <Banner kind="warn" label={t("pick.otherLot")} message={t("pick.otherLotMsg", { lot: otherLot.container.lot_number ?? "?", proposed: otherLot.proposed })} />
            <Chips options={reasons.length > 0 ? reasons : [{ value: "other", label: t("pick.otherReason") }]} value={otherReason} onChange={setOtherReason} />
          </>
        )}
        {!isPalletStep && !split && !otherLot && view === "order" && (
          <>
            <Prompt step={`${order.reference} · ${order.customer?.name ?? ""}`} title={t("pick.chooseLine")} sub={t("pick.suggested")} />
            {orderedLines.map((l) => {
              const p = prop(l.lineNo);
              const got = pickedFor(l.lineNo);
              const full = remaining(l.lineNo) === 0;
              return (
                <div key={l.lineNo} className={`row${full ? " done" : ""}${l.lineNo === current ? " now" : ""}`} onClick={(e) => { e.stopPropagation(); setLineNo(l.lineNo); setView("line"); }}>
                  <span className="k" style={full ? { color: "var(--muted)" } : undefined}>{l.productName ?? l.description ?? l.sku ?? ""}</span>
                  <span className="r2"><small>{[p?.items[0]?.code, l.sku, p?.items[0]?.lotNumber, group ? origOf(group, l.lineNo)?.reference : null].filter(Boolean).join(" · ")}</small><span className="q">{fmtNum(got)} / {fmtQty(l.quantity, l.uom)}</span></span>
                </div>
              );
            })}
            <Banner kind="ok" label={`${t("unit.pallet")} ${palletIndex} · ${kolliOnPallet} ${t("pick.kolli")}`} message="" />
          </>
        )}
        {!isPalletStep && !split && !otherLot && view === "line" && curLine && (
          <>
            <Prompt step={t("pick.step3", { n: lineIndex, total: lines.length, p: palletIndex })} title={t("pick.goTo")} />
            {curItem ? (
              // The box in 3D when the site's published layout knows the place (owner 2026-11-02); today's box otherwise.
              curItem.code ? (
                <GotoScene code={curItem.code} sub={curItem.path?.split("/").slice(0, -1).join(" · ") ?? null} fallback={<div className="proposal"><div className="loc">{curItem.code}</div><div className="why">{curItem.path?.split("/").slice(0, -1).join(" · ") ?? ""}</div></div>} />
              ) : (
                <div className="proposal"><div className="loc">—</div><div className="why">{curItem.path?.split("/").slice(0, -1).join(" · ") ?? ""}</div></div>
              )
            ) : (
              <div className="label"><div className="empty">{curProp?.short ? t("pick.short", { n: fmtQty(curProp.short, curLine.uom) }) : t("pick.lineFull")}</div></div>
            )}
            <div className="take">
              <span className="cap">{t("pick.left")}</span>
              <span className="n">{fmtNum(remaining(curLine.lineNo))}</span><span className="u">{fmtQty(1, curLine.uom).replace(/^[\d\s,.]+/, "")}</span>
              {curItem && <div className="lot">{curProp?.pallet ? t("pick.wholePallet", { n: curProp.pallet.children }) : curItem.kind === "loose" ? t("pick.bulk") : t("pick.batch")}<b>{curProp?.pallet?.sscc ? shortSscc(curProp.pallet.sscc) : curItem.kind === "loose" ? `${fmtQty(curItem.quantity, curItem.uom)}` : curItem.lotNumber ?? "—"}</b>{curItem.bestBefore ? t("pick.bestBefore", { date: curItem.bestBefore }) : ""}</div>}
            </div>
            <ScanBar inputRef={inputRef} placeholder={curProp?.pallet ? t("pick.scanPallet") : curItem?.kind === "loose" || (curLine.productId !== null && isBulkProduct(registry, registry.bundle.products.find((p) => p.id === curLine.productId) ?? null)) ? t("pick.scanOrType") : t("pick.scanItem")} onCamera={() => { unlockAudio(); setCamera(true); }} />
            <div className="label">
              <div className="head"><b>{curLine.productName ?? curLine.description ?? ""}</b><span>{[curLine.sku, group ? t("pick.orderTag", { ref: origOf(group, curLine.lineNo)?.reference ?? "" }) : null, curLine.customerLabel?.carton ? t("pick.customerLabel") : null].filter(Boolean).join(" · ")}</span></div>
              <div className="rows">
                {curItem?.gtin && <div className="cell"><div className="t">GTIN</div><div className="v">{curItem.gtin}</div></div>}
                <div className="cell"><div className="t">{t("pick.takeHere")}</div><div className="v">{curProp?.items.filter((i) => i.code === curItem?.code).length ?? 0} <small>{t("pick.kolli")}</small></div></div>
                {curItem && <div className="cell"><div className="t">{t("pick.onLocation")}</div><div className="v">{curItem.locationRoots} <small>{t("pick.kolli")}</small></div></div>}
              </div>
            </div>
          </>
        )}
      </div>
      <Actions>
        {isPalletStep && (
          <>
            <Btn kind="quiet" icon="back" grow={false} onClick={() => (earlier.length === 0 ? onAbort() : setSheet("abandon"))} />
            {closedPallets.length > 0 && !orderComplete && <Btn kind="secondary" onClick={() => void finishShort()}>{t("pick.done")}</Btn>}
            <Btn kind="primary" onClick={() => void newPallet()}>{t("pick.newPallet")}</Btn>
          </>
        )}
        {!isPalletStep && split && (
          <>
            <Btn kind="quiet" icon="back" grow={false} onClick={() => setSplit(null)} />
            <Btn kind="primary" disabled={!(Number(splitAmount) > 0) || Number(splitAmount) > split.have} onClick={confirmSplit}>{t("pick.splitDo")}</Btn>
          </>
        )}
        {!isPalletStep && otherLot && (
          <>
            <Btn kind="secondary" onClick={() => { setOtherLot(null); setBanner(null); }}>{t("pick.takeProposed")}</Btn>
            <Btn kind="primary" disabled={reasons.length > 0 && !otherReason} onClick={() => { const o = otherLot; setOtherLot(null); takeCarton(o.container, o.elements, o.line, otherReason ?? "other", o.container.parent_id && (!pallet || o.container.parent_id !== pallet.containerId) ? o.container.parent_id : null); }}>{t("pick.pickAnyway")}</Btn>
          </>
        )}
        {!isPalletStep && !split && !otherLot && (
          <>
            <Btn kind="quiet" icon="back" grow={false} onClick={() => { if (view === "line") setView("order"); else if (takes.length === 0 && earlier.length === 0) onAbort(); else setSheet("abandon"); }} />
            {view === "line" && curLine?.productId && canManual(registry.bundle.siteConfig?.manual, "pick") && <Btn kind="secondary" onClick={() => {
              // A bulk line has no batch to look up: "Utan scanning" is the place-and-quantity path (owner 2026-11-03).
              const lp = registry.bundle.products.find((p) => p.id === curLine.productId) ?? null;
              setManualQ(""); setManualNote(""); setManualStock(null); setSheet("manual");
              if (!(lp && isBulkProduct(registry, lp))) Api.stock(curLine.productId!).then(setManualStock).catch(() => setManualStock([]));
            }}>{t("pick.manual")}</Btn>}
            {!(view === "order" && orderComplete) && <Btn kind="secondary" disabled={busy || takes.length === 0} onClick={() => void closePallet()}>{t("pick.palletDone")}</Btn>}
            {view === "order" && orderComplete ? (
              <Btn kind="primary" disabled={busy || takes.length === 0} onClick={() => void closePallet()}>{t("pick.palletDoneFinish")}</Btn>
            ) : view === "order" ? (
              <Btn kind="primary" onClick={() => setView("line")}>{t("pick.continueLine", { n: lineIndex })}</Btn>
            ) : (
              <Btn kind="primary" onClick={() => { const next = orderedLines.find((l) => l.lineNo !== current && remaining(l.lineNo) > 0); if (next) setLineNo(next.lineNo); else setView("order"); }}>{t("pick.nextLine")}</Btn>
            )}
          </>
        )}
      </Actions>
      {bulkWhere && (
        <Sheet title={t("pick.bulkWhere", { product: bulkWhere.product.name })} onClose={() => setBulkWhere(null)}>
          <p className="hint">{t("pick.bulkWhereSub")}</p>
          <div className="list">
            {bulkWhere.places.map((pl) => {
              const n = lineFor(bulkWhere.product.id);
              return <ListItem key={pl.locationId} title={pl.code} desc={`${fmtQty(String(pl.quantity), pl.uom)}${pl.proposed ? ` · ${t("pick.proposed")}` : ""}`} onClick={() => { if (n !== null) startBulkAt(bulkWhere.product, bulkWhere.elements, n, pl.row, bulkWhere.manual); }} />;
            })}
          </div>
        </Sheet>
      )}
      {zeroAsk && (
        <Sheet title={t("pick.zeroTitle", { code: zeroAsk.code })} onClose={() => setZeroAsk(null)}>
          <p>{t("pick.zeroMsg")}</p>
          <div className="chips">
            <button type="button" className="chip on" onClick={() => void zeroAnswer(true)}>{t("pick.zeroYes")}</button>
            <button type="button" className="chip" onClick={() => void zeroAnswer(false)}>{t("pick.zeroNo")}</button>
          </div>
        </Sheet>
      )}
      {sheet === "manual" && curLine && curProp && (
        <Sheet title={t("pick.manualTitle", { product: curLine.productName ?? curLine.sku ?? "" })} onClose={() => setSheet(null)}>
          {(() => { const lp = registry.bundle.products.find((p) => p.id === curLine.productId) ?? null; const bulkLine = !!lp && isBulkProduct(registry, lp); return (<>
          <p className="hint">{bulkLine ? t("pick.manualBulkSub") : t("pick.manualSub")}</p>
          <Entry value={manualNote} onChange={setManualNote} onEnter={() => { if (bulkLine && lp && manualNote.trim()) { setSheet(null); const g = lp.gtins[0]?.gtin ?? null; void startBulk(lp, { ...plainElements(lp.sku, null), gtin: g, ai: g ? { "01": g } : {} }, null, manualNote.trim()); } }} inputMode="text" placeholder={t("pick.manualWhy")} tag={t("common.required")} />
          {!manualNote.trim() && <p className="hint" style={{ color: "var(--warn)" }}>{t("pick.manualNeedsWhy")}</p>}
          {bulkLine && lp && <Actions><Btn kind="primary" disabled={!manualNote.trim()} onClick={() => { setSheet(null); const g = lp.gtins[0]?.gtin ?? null; void startBulk(lp, { ...plainElements(lp.sku, null), gtin: g, ai: g ? { "01": g } : {} }, null, manualNote.trim()); }}>{t("pick.continue")}</Btn></Actions>}
          {!bulkLine && (<>
          <Entry value={manualQ} onChange={setManualQ} onEnter={() => undefined} inputMode="text" placeholder={t("pick.manualSearch")} autoFocus={false} />
          {manualStock === null && <p className="hint">{t("common.loading")}</p>}
          <div className="list">
            {(manualStock ?? [])
              .filter((r) => r.kind === "trade-item" && (r.state === "received" || r.state === "stored") && !allTakes.some((x) => x.containerId === r.container_id) && (!manualQ || (r.lot_number ?? "").toLowerCase().includes(manualQ.toLowerCase()) || (r.location_code ?? "").toLowerCase().includes(manualQ.toLowerCase())))
              .sort((a, b) => (a.best_before ?? "9").localeCompare(b.best_before ?? "9") || (a.location_path ?? "").localeCompare(b.location_path ?? ""))
              .slice(0, 30)
              .map((r) => {
                const proposed = curProp.items.some((i) => i.containerId === r.container_id);
                const tooShort = !!(r.best_before && order.minShelfLifeDays && new Date(r.best_before) < new Date(Date.now() + order.minShelfLifeDays * 86_400_000));
                return <ListItem key={r.container_id} title={`${r.lot_number ?? "—"}${r.best_before ? ` · ${t("pick.bestBefore")} ${r.best_before}` : ""}`} desc={`${r.location_code ?? "?"} · ${fmtQty(r.quantity, r.uom)}${proposed ? ` · ${t("pick.proposed")}` : ""}${tooShort ? ` · ${t("pick.manualTooShort")}` : ""}`} off={!manualNote.trim()} onClick={manualNote.trim() ? () => void manualPick(r.container_id) : undefined} />;
              })}
            {manualStock && manualStock.length === 0 && <p className="hint">{t("pick.manualNone")}</p>}
          </div>
          </>)}
          </>); })()}
        </Sheet>
      )}
      {sheet === "abandon" && (
        <Sheet title={t("more.abandon")} onClose={() => setSheet(null)}>
          <p>{t("pick.abandonConfirm")}</p>
          <Btn kind="danger" onClick={onAbort}>{t("more.abandonYes")}</Btn>
        </Sheet>
      )}
    </div>
  );
}

/** The pallet's step batch: Packed when the pallet was issued here, Disaggregated for cartons
 *  lifted off stock pallets, Split for pieces, Picked per carton, then the flow's Aggregated. */
function buildPickBatch(state: RunState, pallet: Pallet, takes: Take[], ctx: { settings: ReturnType<typeof getSettings>; goodsOwnerId: string; orderId: string; orderIdOf: (lineNo: number) => string; lineNoOf: (lineNo: number) => number; products: Map<string, { id: string }>; outboundLocationId: string | null }): StepBatch {
  const batchId = uuidv7();
  const base = (type: EventEnvelope["type"], payload: Record<string, unknown>, at: string, capture: "barcode" | "system" | "manual" = "barcode", captureDetail: string | null = null): EventEnvelope => ({
    eventId: uuidv7(),
    tenantId: ctx.settings.tenantId,
    goodsOwnerId: ctx.goodsOwnerId,
    type,
    class: classOf(type),
    schemaVersion: 1,
    occurredAt: at,
    actorId: ctx.settings.actorId,
    deviceId: ctx.settings.deviceId,
    capture,
    captureDetail,
    readPoint: null,
    bizLocation: null,
    flowId: FLOW.id,
    flowVersion: FLOW.version,
    batchId,
    payload,
  });
  const events: EventEnvelope[] = [];
  const now = new Date().toISOString();
  if (pallet.fresh) {
    events.push(base("packed", { containerId: pallet.containerId, identity: { scheme: "sscc", key: `sscc:00=${pallet.sscc}`, ai: { "00": pallet.sscc }, origin: "tenant" }, sscc: pallet.sscc, locationId: null, salesOrderId: ctx.orderId }, now, "system"));
  }
  const lifted = new Map<string, string[]>();
  for (const x of takes) {
    if (x.fromPalletId) lifted.set(x.fromPalletId, [...(lifted.get(x.fromPalletId) ?? []), x.split ? x.split.parentContainerId : x.containerId]);
  }
  for (const [parent, kids] of lifted) events.push(base("disaggregated", { parentContainerId: parent, childContainerIds: kids }, now));
  // The closed pallet leaves for the outbound area (site setting picking.outboundLocationId; owner 2026-11-02:
  // the storage place must be free at once). Before the picks: a stock pallet taken whole moves with its
  // cartons, cartons aggregated later take the pallet's place; the Picked events then set their state.
  if (ctx.outboundLocationId) events.push(base("moved", { containerIds: [pallet.containerId], fromLocationId: null, toLocationId: ctx.outboundLocationId, proposedLocationId: null, overrideReason: null }, now, "system"));
  for (const x of takes) {
    if (x.split) {
      events.push(base("split", {
        parentContainerId: x.split.parentContainerId,
        childContainerId: x.containerId,
        identity: { scheme: x.split.identity.ai["21"] ? "gtin+lot+serial" : "gtin+lot", key: x.split.identity.key, ai: x.split.identity.ai, origin: "tenant" },
        productId: null,
        gtin: x.split.identity.ai["01"] ?? null,
        lot: x.split.identity.ai["10"] ? { number: x.split.identity.ai["10"], bestBefore: null, expiry: null, productionDate: null, packagingDate: null } : null,
        serial: x.split.identity.ai["21"] ?? null,
        quantity: { value: str(x.quantity), uom: x.uom, source: "entered" },
        remaining: { value: str(x.split.remaining), uom: x.uom, source: "inferred" },
        locationId: null,
        reason: null,
      }, x.at, "system"));
    }
    events.push(base("picked", { containerIds: [x.containerId], salesOrderId: ctx.orderIdOf(x.lineNo), lineNo: ctx.lineNoOf(x.lineNo), palletContainerId: pallet.containerId, overrideReason: x.overrideReason }, x.at, x.manual ? "manual" : "barcode", x.manual ? `manuell: ${x.manual}` : null));
  }
  for (const em of state.emitted) events.push(base(em.event, em.payload as Record<string, unknown>, now, "system"));
  return { flowId: FLOW.id, flowVersion: FLOW.version, stepId: "items", events, effects: [] };
}
